import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { CancelOfflinePeriodConfirmationDialog } from 'widgets/OpeningTimes/OfflinePeriods/CancelOfflinePeriodConfirmationDialog';
import { OfflinePeriodAcceptedDateFormat } from 'widgets/OpeningTimes/OfflinePeriods/constants/OfflinePeriodAcceptedDateFormat';
import { OfflinePeriodsEvents } from 'widgets/OpeningTimes/OfflinePeriods/constants/OfflinePeriodsEvents';

import { BinOutlined } from '@bolteu/kalep-react-icons';
import { NetworkRequestState } from '@bolteu/redux-session';

import { selectSelectedProvider } from 'rdx/account';
import {
  cancelOfflinePeriodAsyncAction,
  selectOfflinePeriodsCancelError,
  selectOfflinePeriodsCancelRequestState,
} from 'rdx/offlinePeriods';

import { useCommonAppServices } from 'root/CommonAppServicesContext';

import {
  CancelProviderOfflinePeriodRequest,
  ProviderOfflinePeriod,
} from '../../../../../common/all/api/gen/DeliveryProviderPortalAuthApi';
import { SolidColors } from '../../../../../common/all/styles/bolt-uikit-colors';

export interface OfflinePeriodsListProps {
  offlinePeriods: ProviderOfflinePeriod[];
}

export interface OfflinePeriodCompProps {
  offlinePeriod: ProviderOfflinePeriod;
  onRemoveOfflinePeriod: (offlinePeriod: ProviderOfflinePeriod) => void;
}

export const OfflinePeriodComp: React.FC<OfflinePeriodCompProps> = memo(
  ({ offlinePeriod, onRemoveOfflinePeriod }) => {
    const { formatMessage } = useIntl();
    const [isCancelOfflinePeriodConfirmationOpen, setCancelOfflinePeriodConfirmationOpen] =
      useState(false);

    const generateDatesToShow = useCallback(() => {
      const startDate = DateTime.fromFormat(
        offlinePeriod.start_date,
        OfflinePeriodAcceptedDateFormat,
      );
      const endDate = DateTime.fromFormat(offlinePeriod.end_date, OfflinePeriodAcceptedDateFormat);
      if (startDate.hasSame(endDate, 'day')) {
        return startDate.toFormat('dd.MM.yyyy');
      }

      return `${startDate.toFormat('dd.MM.yyyy')} - ${endDate.toFormat('dd.MM.yyyy')}`;
    }, [offlinePeriod]);

    const removeOfflinePeriod = useCallback(() => {
      onRemoveOfflinePeriod(offlinePeriod);
    }, [offlinePeriod, onRemoveOfflinePeriod]);

    const openCancelOfflinePeriodConfirmationDialog = useCallback(() => {
      setCancelOfflinePeriodConfirmationOpen(true);
    }, []);

    const closeCancelOfflinePeriodConfirmationDialog = useCallback(() => {
      setCancelOfflinePeriodConfirmationOpen(false);
    }, []);

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        sx={{
          backgroundColor: SolidColors.neutral200,
          borderRadius: 2,
          border: '1px',
          padding: '10px',
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="left">
          <Box>
            <Typography fontWeight="bold">{generateDatesToShow()}</Typography>
          </Box>
          <Box>
            <Typography>{formatMessage({ id: 'offlinePeriods.closed' })}</Typography>
          </Box>
        </Box>
        <Box
          marginLeft="auto"
          marginTop="auto"
          marginBottom="auto"
          onClick={openCancelOfflinePeriodConfirmationDialog}
        >
          <BinOutlined />
        </Box>
        {isCancelOfflinePeriodConfirmationOpen && (
          <CancelOfflinePeriodConfirmationDialog
            isOpen={isCancelOfflinePeriodConfirmationOpen}
            onClose={closeCancelOfflinePeriodConfirmationDialog}
            onConfirm={removeOfflinePeriod}
          />
        )}
      </Box>
    );
  },
);

export const OfflinePeriodsList: React.FC<OfflinePeriodsListProps> = memo(({ offlinePeriods }) => {
  function useActions() {
    const dispatch = useDispatch();
    return useMemo(
      () => ({
        cancelOfflinePeriod: (offlinePeriodToCancel: CancelProviderOfflinePeriodRequest) =>
          dispatch(cancelOfflinePeriodAsyncAction.request(offlinePeriodToCancel)),
      }),
      [dispatch],
    );
  }
  const [offlinePeriodToDeleteId, setOfflinePeriodToDeleteId] = useState<number>(null);
  const actions = useActions();
  const providerId = useSelector(selectSelectedProvider)?.providerId || null;
  const { mixpanelService } = useCommonAppServices();
  const offlinePeriodsCancelRequestState = useSelector(selectOfflinePeriodsCancelRequestState);
  const offlinePeriodsCancelError = useSelector(selectOfflinePeriodsCancelError);
  const handleOfflinePeriodRemoved = useCallback(
    (offlinePeriod: ProviderOfflinePeriod) => {
      setOfflinePeriodToDeleteId(offlinePeriod.id);
      actions.cancelOfflinePeriod({ offline_period_id: offlinePeriod.id, provider_id: providerId });
    },
    [actions, providerId],
  );

  useEffect(() => {
    if (offlinePeriodsCancelRequestState === NetworkRequestState.success) {
      mixpanelService.trackProviderEvent(
        OfflinePeriodsEvents.OFFLINE_PERIOD_CANCEL_BUTTON_CLICKED,
        {
          providerId,
          offline_period_id: offlinePeriodToDeleteId,
          is_success: true,
          error_code: null,
        },
      );
    }

    if (offlinePeriodsCancelRequestState === NetworkRequestState.failure) {
      mixpanelService.trackProviderEvent(
        OfflinePeriodsEvents.OFFLINE_PERIOD_CANCEL_BUTTON_CLICKED,
        {
          providerId,
          offline_period_id: offlinePeriodToDeleteId,
          is_success: false,
          error_code: offlinePeriodsCancelError,
        },
      );
    }
  }, [
    mixpanelService,
    offlinePeriodToDeleteId,
    offlinePeriodsCancelRequestState,
    offlinePeriodsCancelError,
    providerId,
  ]);

  if (!offlinePeriods || !offlinePeriods.length) {
    return null;
  }

  return (
    <Box className="w-full">
      {offlinePeriods.map((offlinePeriod) => (
        <Box mt={1} key={offlinePeriod.id}>
          <OfflinePeriodComp
            offlinePeriod={offlinePeriod}
            onRemoveOfflinePeriod={handleOfflinePeriodRemoved}
          />
        </Box>
      ))}
    </Box>
  );
});
